import { useMotionValue, useTransform } from "motion/react";
import { useRef } from "react";
import useEvent from "react-use-event-hook";

import { useRtl } from "@ryde/redux";
import {
  getRideDrawerMaxContentWidth,
  IRideDrawerDragState,
} from "@ryde/shared";

export interface IDrawerWidthResizeConfig {
  defaultWidth: number;
  minWidth: number;
  keyboardStep: number;
}

/**
 * Single-panel drawer width resize: drag or arrow keys on the drawer's leading edge.
 * Without a config the drawer keeps its fixed width and the handlers do nothing — hooks
 * cannot be called conditionally, so a non-resizable drawer still calls this.
 */
export const useDrawerWidthResize = (
  config: IDrawerWidthResizeConfig | undefined,
) => {
  const { isRtl } = useRtl();
  const drawerWidthMotion = useMotionValue(config?.defaultWidth ?? 0);
  const dragRef = useRef<IRideDrawerDragState | null>(null);
  const pendingWidthRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const drawerWidthStyle = useTransform(() => `${drawerWidthMotion.get()}px`);

  const clampWidth = ({
    width,
    minWidth,
  }: {
    width: number;
    minWidth: number;
  }): number =>
    Math.max(minWidth, Math.min(getRideDrawerMaxContentWidth(), width));

  const flushWidth = useEvent(() => {
    animationFrameRef.current = null;
    if (pendingWidthRef.current !== null) {
      drawerWidthMotion.set(pendingWidthRef.current);
      pendingWidthRef.current = null;
    }
  });

  const handleDrag = (event: MouseEvent) => {
    if (!dragRef.current || !config) {
      return;
    }

    const { startX, initialWidth } = dragRef.current;
    const deltaX = event.clientX - startX;
    const delta = isRtl ? deltaX : -deltaX;

    pendingWidthRef.current = clampWidth({
      width: initialWidth + delta,
      minWidth: config.minWidth,
    });

    if (animationFrameRef.current === null) {
      animationFrameRef.current = requestAnimationFrame(flushWidth);
    }
  };

  const handleDragEnd = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
      flushWidth();
    }

    dragRef.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", handleDrag);
    document.removeEventListener("mouseup", handleDragEnd);
  };

  const onResizeHandleMouseDown = (event: React.MouseEvent) => {
    if (!config) {
      return;
    }

    event.preventDefault();
    dragRef.current = {
      startX: event.clientX,
      initialWidth: drawerWidthMotion.get(),
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("mouseup", handleDragEnd);
  };

  const onResizeHandleKeyDown = (event: React.KeyboardEvent) => {
    if (!config || (event.key !== "ArrowLeft" && event.key !== "ArrowRight")) {
      return;
    }

    event.preventDefault();

    const isExpand = isRtl
      ? event.key === "ArrowRight"
      : event.key === "ArrowLeft";

    drawerWidthMotion.set(
      clampWidth({
        width:
          drawerWidthMotion.get() +
          (isExpand ? config.keyboardStep : -config.keyboardStep),
        minWidth: config.minWidth,
      }),
    );
  };

  return {
    drawerWidthStyle,
    onResizeHandleMouseDown,
    onResizeHandleKeyDown,
  };
};
